import React from "react";
import { Activity } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { endpoints } from "@/lib/api";
import { Panel, PageHeader, Loading, Kpi, StatusBadge } from "@/components/common";
import { inr, num, statusOf } from "@/lib/format";

function HealthBar({ pct }) {
  const color = pct < 55 ? "#ef4444" : pct < 72 ? "#f59e0b" : pct < 82 ? "#0ea5e9" : "#10b981";
  return (
    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
      <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

export default function AssetHealth() {
  const { data } = usePoll(endpoints.assets, 6000);
  if (!data) return <Loading />;
  const s = data.summary;

  return (
    <div data-testid="asset-health-page">
      <PageHeader icon={Activity} title="Asset Health Center"
        subtitle="Maintenance view — health, remaining life, efficiency and ₹ impact per asset." />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Kpi testid="asset-avg-health" label="Avg Health" value={s.avg_health} unit="%" accent="#10b981" />
        <Kpi testid="asset-critical" label="Critical" value={s.critical} accent={s.critical ? "#ef4444" : "#10b981"} />
        <Kpi testid="asset-warning" label="Warning" value={s.warning} accent={s.warning ? "#f59e0b" : "#10b981"} />
        <Kpi testid="asset-watch" label="Watch" value={s.watch ?? 0} accent={s.watch ? "#0ea5e9" : "#10b981"} />
        <Kpi testid="asset-impact" label="Cost Impact" value={inr(s.total_cost_impact_yr)} sub="/ year" accent="#7c3aed" />
      </div>

      <Panel testid="asset-table" title="Asset Health" subtitle="Worst-first — click an asset's twin to drill down">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
              <th className="text-left font-semibold py-2">Asset</th>
              <th className="text-left font-semibold">Type</th>
              <th className="text-left font-semibold w-[180px]">Health</th>
              <th className="text-right font-semibold">Efficiency</th>
              <th className="text-right font-semibold">Remaining Life</th>
              <th className="text-right font-semibold">₹ Impact/yr</th>
              <th className="text-right font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.assets.map((a) => (
              <tr key={a.id} className="border-b border-slate-50" data-testid={`asset-row-${a.id}`}>
                <td className="py-2.5 font-medium text-slate-800">{a.name}</td>
                <td className="text-slate-500">{a.type}</td>
                <td>
                  <div className="flex items-center gap-2">
                    <HealthBar pct={a.health_pct} />
                    <span className="font-mono text-xs w-10 text-right">{a.health_pct}%</span>
                  </div>
                </td>
                <td className="text-right font-mono">{a.efficiency_pct}%</td>
                <td className="text-right font-mono">{a.remaining_life_months} mo</td>
                <td className="text-right font-mono font-semibold text-red-600">{inr(a.cost_impact_yr)}</td>
                <td className="text-right"><StatusBadge status={a.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
